import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../utils/api';
import Card from '../../components/Common/Card';
import Button from '../../components/Common/Button';
import { showSuccess, showError } from '../../utils/swal';
import { Check, X, Clock, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const RequestList = ({ data, emptyMessage, renderItem }) => (
  <div className="divide-y divide-gray-200">
    {data.length === 0 ? (
      <div className="p-8 text-center text-gray-500 dark:text-gray-400">{emptyMessage}</div>
    ) : (
      data.map(renderItem)
    )}
  </div>
);

const ShareRequests = () => {
  const [requests, setRequests] = useState([]);
  const [myRequests, setMyRequests] = useState([]);
  const [approved, setApproved] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('incoming');
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const respPending = await api.get('/portfolio-shares/pending');
      setRequests(respPending.data);

      const respMy = await api.get('/portfolio-shares/my-requests');
      setMyRequests(respMy.data);

      const approved = await api.get('/portfolio-shares/approved');
      setApproved(approved.data);
    } catch (e) {
      console.error('Talepler yüklenemedi', e);
      showError('Talepler yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (id) => {
    try {
      await api.put(`/portfolio-shares/${id}/approve`);
      showSuccess('Talep onaylandı');
      fetchData();
    } catch (e) {
      showError(e.response?.data?.message || 'Hata oluştu');
    }
  };

  const handleReject = async (id) => {
    try {
      await api.put(`/portfolio-shares/${id}/reject`);
      showSuccess('Talep reddedildi');
      fetchData();
    } catch (e) {
      showError(e.response?.data?.message || 'Hata oluştu');
    }
  };

  const handleCancel = async (id) => {
    try {
      await api.put(`/portfolio-shares/${id}/cancel`);
      showSuccess('Talep reddedildi');
      fetchData();
    } catch (e) {
      showError(e.response?.data?.message || 'Hata oluştu');
    }
  };

  if (loading) return <div className="p-8 text-center text-gray-500">Yükleniyor...</div>;

  const renderIncomingItem = (req) => (
    <div key={req.id} className="p-4 sm:px-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
      <div>
        <h3 className="font-semibold text-gray-900 mb-1">
          <button
            className="text-gray-900 dark:text-gray-100 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
            onClick={() => navigate(`/portfolios/${req.portfolio_id}`)}
          >
            {req.portfolio?.title || 'Bilinmeyen Portföy'}
          </button>
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-2">
          <span className="font-medium text-gray-800 dark:text-gray-200">{req.shared_with?.name}</span> adlı çalışan bu portföyü paylaşmak için yetki istiyor.
        </p>
        <p className="text-xs text-gray-400 mt-1">
          Talep Tarihi: {new Date(req.created_at).toLocaleDateString()}
        </p>
      </div>
      <div className="flex gap-2">
        <Button
          size="sm"
          variant="outline"
          onClick={() => navigate(`/portfolios/${req.portfolio_id}`)}
        >
          <ExternalLink size={16} />
        </Button>
        <Button
          size="sm"
          variant="success"
          onClick={() => handleApprove(req.id)}
        >
          <Check size={16} className="mr-1" /> Onayla
        </Button>
        <Button
          size="sm"
          variant="danger"
          onClick={() => handleReject(req.id)}
        >
          <X size={16} className="mr-1" /> Reddet
        </Button>
      </div>
    </div>
  );

  const renderApprovedItem = (req) => (
    <div key={req.id} className="p-4 sm:px-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
      <div>
        <h3 className="font-semibold text-gray-900 dark:text-white mb-1" >
          <button
            className="text-gray-900 dark:text-gray-100 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
            onClick={() => navigate(`/portfolios/${req.portfolio_id}`)}
          >
            {req.portfolio?.title || 'Bilinmeyen Portföy'}
          </button>
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-2">
          <span className="font-medium text-gray-800 dark:text-gray-200">{req.shared_with?.name}</span> adlı çalışan bu portföyü paylaşmak için yetkisi bulunuyor.
        </p>
        <p className="text-xs text-gray-400 mt-1">
          Talep Tarihi: {new Date(req.created_at).toLocaleDateString()}
        </p>
      </div>
      <div className="flex gap-2">
        <Button
          size="sm"
          variant="outline"
          onClick={() => navigate(`/portfolios/${req.portfolio_id}`)}
        >
          <ExternalLink size={16} />
        </Button>
        <Button
          size="sm"
          variant="danger"
          onClick={() => handleCancel(req.id)}
        >
          <X size={16} className="mr-1" /> Paylaşımı Bitir
        </Button>
      </div>
    </div>
  );

  const renderOutgoingItem = (req) => (
    <div key={req.id} className="p-4 sm:px-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
      <div>
        <h3 className="font-semibold text-gray-900 mb-1">
          <button
            className="text-gray-900 dark:text-gray-100 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
            onClick={() => navigate(`/portfolios/${req.portfolio_id}`)}
          >
            {req.portfolio?.title || 'Bilinmeyen Portföy'}
          </button>
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-1">
          Sahibi: <span className="font-medium text-gray-800 dark:text-gray-200">{req.shared_by?.name || '-'}</span>
        </p>
        <p className="text-xs text-gray-400 mt-1">
          Talep Tarihi: {new Date(req.created_at).toLocaleDateString()}
        </p>
      </div>
      <div className="flex gap-4 items-center">
        <div className="text-right">
          {req.status === 'pending' && <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300"><Clock size={12} className="mr-1" /> Bekliyor</span>}
          {req.status === 'approved' && (
            <div className="flex flex-col items-end gap-1">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
                <Check size={12} className="mr-1" /> Onaylandı
              </span>
              {req.portfolio?.visibility === 'shared' && (
                <span className="text-[10px] text-green-600 font-medium">Otomatik Onay (Ofise Açık)</span>
              )}
            </div>
          )}
          {req.status === 'rejected' && <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300"><X size={12} className="mr-1" /> Reddedildi</span>}
          {req.status === 'canceled' && <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300"><X size={12} className="mr-1" /> İptal Edildi</span>}
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => navigate(`/portfolios/${req.portfolio_id}`)}
        >
          <ExternalLink size={16} />
        </Button>
        {req.status === 'approved' && req.share_token && (
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              const baseUrl = window.location.origin.replace('http://', 'https://');
              navigator.clipboard.writeText(window.location.origin + '/p/' + req.share_token);
              showSuccess('Link kopyalandı');
            }}
          >
            Linki Kopyala
          </Button>
        )}
      </div>
    </div>
  );

  const tabContents = {
    incoming: <RequestList data={requests} emptyMessage="Bekleyen gelen talep bulunmuyor." renderItem={renderIncomingItem} />,
    outgoing: <RequestList data={myRequests} emptyMessage="Gönderdiğiniz bir talep bulunmuyor." renderItem={renderOutgoingItem} />,
    approved: <RequestList data={approved} emptyMessage="İzin verdiğiniz bir portföy bulunmuyor." renderItem={renderApprovedItem} />,
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Paylaşım Talepleri</h1>
        <div className="flex bg-gray-100 dark:bg-gray-800 p-1 rounded-lg">
          <button
            className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${activeTab === 'incoming' ? 'bg-white dark:bg-gray-700 shadow text-gray-900 dark:text-white' : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            onClick={() => setActiveTab('incoming')}
          >
            Gelen Talepler ({requests.length})
          </button>
          <button
            className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${activeTab === 'outgoing' ? 'bg-white dark:bg-gray-700 shadow text-gray-900 dark:text-white' : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            onClick={() => setActiveTab('outgoing')}
          >
            Giden Taleplerim ({myRequests.length})
          </button>
          <button
            className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${activeTab === 'approved' ? 'bg-white dark:bg-gray-700 shadow text-gray-900 dark:text-white' : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            onClick={() => setActiveTab('approved')}
          >
            İzin Verdiklerim ({approved.length})
          </button>
        </div>
      </div>

      <Card noPadding>
        {tabContents[activeTab]}
      </Card>
    </div>
  );
};

export default ShareRequests;
